<template>
  <v-app>
    <v-main>
      <div class="page">
        <div class="page-bg" aria-hidden="true"></div>
        <AppHeader
          :user-label="userLabel"
          :is-anonymous="isAnonymous"
          :avatar-url="userAvatar"
          :credits="isAuthenticated ? credits : null"
          :plan-id="currentPlanId"
          :is-admin="isAdminUser"
          @sign-in="handleGoogleLogin"
          @sign-out="handleSignOut"
          @buy-credits="openCreditsDialog"
          @navigate-history="scrollToHistory('history')"
          @navigate-saved="scrollToHistory('saved')"
          @navigate-admin="navigateAdmin"
          @navigate-home="navigateHome"
        />
        <HeroSection
          v-if="!showAdmin"
          :is-authenticated="isAuthenticated"
          @sign-in="handleGoogleLogin"
          @how-it-works="openHowItWorks"
        />

        <div class="page-content content-shell">
          <template v-if="showAdmin">
            <AdminDashboard :stats="adminStats" />
          </template>
          <template v-else-if="isAuthenticated">
            <div class="generator-toggle">
              <div class="toggle-label">
                {{ t('generation.selectorLabel') }}
              </div>
              <v-btn-toggle
                v-model="generationType"
                mandatory
                variant="outlined"
                class="toggle-group"
              >
                <v-btn value="interior">{{ t('generation.interior') }}</v-btn>
                <v-btn value="outdoor">{{ t('generation.outdoor') }}</v-btn>
                <v-btn value="manual">{{ t('generation.manual') }}</v-btn>
              </v-btn-toggle>
            </div>

            <RedesignForm
              v-if="generationType === 'interior'"
              :files="interiorFiles"
              :room-type="roomType"
              :selected-style="style"
              :palette="palette"
              :strength="strength"
              :keep-layout="keepLayout"
              :aspect-ratio="interiorAspectRatio"
              :resolution="interiorResolution"
              :rearrange-furniture="rearrangeFurniture"
              :change-electronics="changeElectronics"
              :remove-watermarks="removeWatermarks"
              :custom-instructions="interiorCustomInstructions"
              :room-type-items="roomTypeItems"
              :style-items="styleItems"
              :palette-items="paletteItems"
              :strength-items="strengthItems"
              :aspect-ratio-items="interiorAspectRatioItems"
              :resolution-items="resolutionItems"
              :file-rules="fileRules"
              :busy="busy"
              :can-generate="canGenerate"
              :is-processing="isProcessing"
              :is-authenticated="isAuthenticated"
              :uploading="interiorUploading"
              :credits="credits"
              :credits-required="creditsRequired"
              :has-credits="hasEnoughCredits"
              :error-message="errorMessage"
              @buy-credits="openCreditsDialog"
              @update:files="updateInteriorFiles"
              @update:roomType="updateRoomType"
              @update:style="updateStyle"
              @update:palette="updatePalette"
              @update:strength="updateStrength"
              @update:keepLayout="updateKeepLayout"
              @update:aspectRatio="updateInteriorAspectRatio"
              @update:resolution="updateInteriorResolution"
              @update:rearrangeFurniture="updateRearrangeFurniture"
              @update:changeElectronics="updateChangeElectronics"
              @update:removeWatermarks="updateRemoveWatermarks"
              @update:customInstructions="updateInteriorCustomInstructions"
              @generate="handleGenerate"
              @reset="resetForm"
            />

            <OutdoorForm
              v-else-if="generationType === 'outdoor'"
              :files="outdoorFiles"
              :outdoor-type="outdoorType"
              :selected-style="outdoorStyle"
              :palette="outdoorPalette"
              :strength="outdoorStrength"
              :keep-layout="outdoorKeepLayout"
              :aspect-ratio="outdoorAspectRatio"
              :resolution="outdoorResolution"
              :custom-instructions="outdoorCustomInstructions"
              :outdoor-type-items="outdoorTypeItems"
              :style-items="outdoorStyleItems"
              :palette-items="paletteItems"
              :strength-items="strengthItems"
              :aspect-ratio-items="outdoorAspectRatioItems"
              :resolution-items="resolutionItems"
              :file-rules="fileRules"
              :busy="busy"
              :can-generate="canGenerate"
              :is-processing="isProcessing"
              :is-authenticated="isAuthenticated"
              :uploading="outdoorUploading"
              :credits="credits"
              :credits-required="creditsRequired"
              :has-credits="hasEnoughCredits"
              :error-message="errorMessage"
              @buy-credits="openCreditsDialog"
              @update:files="updateOutdoorFiles"
              @update:outdoorType="updateOutdoorType"
              @update:style="updateOutdoorStyle"
              @update:palette="updateOutdoorPalette"
              @update:strength="updateOutdoorStrength"
              @update:keepLayout="updateOutdoorKeepLayout"
              @update:aspectRatio="updateOutdoorAspectRatio"
              @update:resolution="updateOutdoorResolution"
              @update:customInstructions="updateOutdoorCustomInstructions"
              @generate="handleGenerate"
              @reset="resetForm"
            />

            <ManualForm
              v-else
              :files="manualFiles"
              :prompt="manualPrompt"
              :aspect-ratio="manualAspectRatio"
              :resolution="manualResolution"
              :aspect-ratio-items="manualAspectRatioItems"
              :resolution-items="resolutionItems"
              :file-rules="fileRules"
              :busy="busy"
              :can-generate="canGenerate"
              :is-processing="isProcessing"
              :is-authenticated="isAuthenticated"
              :uploading="manualUploading"
              :credits="credits"
              :credits-required="creditsRequired"
              :has-credits="hasEnoughCredits"
              :error-message="errorMessage"
              @update:files="updateManualFiles"
              @update:prompt="updateManualPrompt"
              @update:aspectRatio="updateManualAspectRatio"
              @update:resolution="updateManualResolution"
              @generate="handleGenerate"
              @reset="resetForm"
              @buy-credits="openCreditsDialog"
            />
            <section ref="historySection" class="history-section mt-12">
              <div class="history-header">
                <h2>
                  {{
                    historyView === 'saved'
                      ? t('saved.title')
                      : t('history.title')
                  }}
                </h2>
                <v-btn-toggle
                  v-model="historyView"
                  mandatory
                  variant="outlined"
                  class="history-toggle"
                >
                  <v-btn value="history">
                    {{ t('history.title') }} ({{ historyTotal }})
                  </v-btn>
                  <v-btn value="saved">
                    {{ t('saved.title') }} ({{ savedTotal }})
                  </v-btn>
                </v-btn-toggle>
              </div>
              <div v-show="historyView === 'history'">
                <JobGallery
                  :history="history"
                  :chip-color="chipColor"
                  :busy="busy"
                  :has-credits="hasCredits"
                  :can-load-more="historyHasMore"
                  :loading-more="historyLoading"
                  :active-job-id="activeJobId"
                  :saved-paths="savedPaths"
                  @edit="handleEditGenerate"
                  @retry="handleRetry"
                  @delete="handleDeleteJob"
                  @load-more="loadMoreHistory"
                  @save-image="handleSaveImage"
                  @remove-image="handleRemoveImage"
                />
              </div>
              <div v-show="historyView === 'saved'">
                <SavedImagesGallery
                  :items="savedImages"
                  :can-load-more="savedHasMore"
                  :loading-more="savedLoading"
                  @remove="(imagePath) => handleRemoveImage({ imagePath })"
                  @details="openSavedDetails"
                  @load-more="loadMoreSavedImages"
                />
              </div>
            </section>
          </template>

          <div v-else class="preview-section">
            <div class="generator-toggle preview-toggle">
              <div class="toggle-label">
                {{ t('generation.selectorLabel') }}
              </div>
              <v-btn-toggle
                v-model="previewType"
                mandatory
                variant="outlined"
                class="toggle-group"
              >
                <v-btn value="interior">{{ t('generation.interior') }}</v-btn>
                <v-btn value="outdoor">{{ t('generation.outdoor') }}</v-btn>
              </v-btn-toggle>
            </div>
            <ImageCompare
              :before-src="previewBefore"
              :after-src="previewAfter"
              :before-src-set="previewBeforeSet"
              :after-src-set="previewAfterSet"
              :sizes="previewSizes"
              :priority="true"
            />
            <section id="how-it-works" class="how-section">
              <h2>{{ t('howItWorks.title') }}</h2>
              <ol class="how-steps">
                <li
                  v-for="(step, index) in howItWorksSteps"
                  :key="index"
                  class="how-step"
                >
                  <span class="step-index">{{ index + 1 }}</span>
                  <p>{{ step }}</p>
                </li>
              </ol>
            </section>
          </div>
        </div>
      </div>
    </v-main>

    <CreditsDialog
      v-model="creditsDialogOpen"
      :plans="subscriptionPlans"
      :busy="busy"
      :error-message="creditsError"
      :current-plan-id="currentPlanId"
      :billing-period-end="billingPeriodEnd"
      :manage-busy="manageBusy"
      @subscribe="handleBuyCredits"
      @manage-subscription="handleManageSubscription"
    />

    <v-dialog v-model="howItWorksOpen" max-width="760">
      <v-card class="how-dialog">
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ t('howItWorks.title') }}</span>
          <v-btn icon variant="text" @click="howItWorksOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <ol class="how-steps how-steps--dialog">
            <li
              v-for="(step, index) in howItWorksSteps"
              :key="index"
              class="how-step"
            >
              <span class="step-index">{{ index + 1 }}</span>
              <p>{{ step }}</p>
            </li>
          </ol>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="checkoutOpen"
      location="top end"
      timeout="5000"
      color="primary"
      variant="tonal"
      @click="checkoutOpen = false"
    >
      {{ checkoutMessage }}
    </v-snackbar>
  </v-app>
</template>

<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { collection, doc } from 'firebase/firestore';
import type {
  DocumentData,
  QueryDocumentSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';

import {
  observeAuth,
  signInWithGoogle,
  signOutUser,
  waitForAuthReady,
  db,
} from './lib/firebase';
import { SUPPORTED_LOCALES, setLocale, type SupportedLocale } from './i18n';
import {
  createJob,
  deleteJob,
  fetchHistoryPage,
  fetchJobById,
  subscribeHistory,
  describeImage,
  uploadInput,
  uploadInputs,
  deleteStoragePath,
} from './lib/jobs';
import {
  fetchSavedImagesPage,
  removeSavedImage,
  saveImage,
  savedImageDocId,
} from './lib/savedImages';
import { ensureUser, subscribeUser } from './lib/users';
import { subscribeAdminStats, type AdminStats } from './lib/admin';
import type { JobWithId } from './types/job';
import type { SubscriptionPlan } from './types/credits';
import type { SavedImage } from './types/saved';
import { createCheckoutSession, createPortalSession } from './lib/payments';
import type { UserCredits } from './types/user';
import {
  ASPECT_RATIOS,
  OUTDOOR_STYLES,
  OUTDOOR_TYPES,
  PALETTES,
  RESOLUTION_TIERS,
  ROOM_TYPES,
  STRENGTHS,
  STYLES,
  type AspectRatio,
  type GenerationType,
  type OutdoorStyle,
  type OutdoorType,
  type Palette,
  type ResolutionTier,
  type RoomType,
  type Strength,
  type Style,
} from './types/presets';
import AppHeader from './components/layout/AppHeader.vue';
import HeroSection from './components/sections/HeroSection.vue';
import AdminDashboard from './components/sections/AdminDashboard.vue';
import ImageCompare from './components/ui/ImageCompare.vue';
import demoBefore from './images/optimized/demo1.webp';
import demoBefore512 from './images/optimized/demo1-512.webp';
import demoBefore768 from './images/optimized/demo1-768.webp';
import demoAfter from './images/optimized/demo2.webp';
import demoAfter512 from './images/optimized/demo2-512.webp';
import demoAfter768 from './images/optimized/demo2-768.webp';
import outdoorDemoBefore from './images/optimized/outdoor-demo.webp';
import outdoorDemoBefore512 from './images/optimized/outdoor-demo-512.webp';
import outdoorDemoBefore768 from './images/optimized/outdoor-demo-768.webp';
import outdoorDemoAfter from './images/optimized/outdoor-demo2.webp';
import outdoorDemoAfter512 from './images/optimized/outdoor-demo2-512.webp';
import outdoorDemoAfter768 from './images/optimized/outdoor-demo2-768.webp';


const RedesignForm = defineAsyncComponent(
  () => import('./components/sections/RedesignForm.vue'),
);
const OutdoorForm = defineAsyncComponent(
  () => import('./components/sections/OutdoorForm.vue'),
);
const ManualForm = defineAsyncComponent(
  () => import('./components/sections/ManualForm.vue'),
);
const JobGallery = defineAsyncComponent(
  () => import('./components/sections/JobGallery.vue'),
);
const SavedImagesGallery = defineAsyncComponent(
  () => import('./components/sections/SavedImagesGallery.vue'),
);
const CreditsDialog = defineAsyncComponent(
  () => import('./components/sections/CreditsDialog.vue'),
);

const { t, locale } = useI18n();
const theme = useTheme();
const isLightTheme = computed(() => theme.global.name.value === 'lightTheme');
const DEFAULT_ASPECT_RATIO: AspectRatio = '1:1';
const DEFAULT_RESOLUTION: ResolutionTier = '1k';

const uid = ref<string | null>(null);
const userLabel = ref<string | null>(null);
const userAvatar = ref<string | null>(null);
const isAnonymous = ref(true);
const isAuthenticated = computed(() => !!uid.value && !isAnonymous.value);
const generationType = ref<GenerationType>('interior');
const previewType = ref<GenerationType>('interior');
const isAdminRoute = ref(false);
const adminStats = ref<AdminStats | null>(null);
const howItWorksOpen = ref(false);
const interiorFiles = ref<File[]>([]);
const outdoorFiles = ref<File[]>([]);
const manualFiles = ref<File[]>([]);
const manualPrompt = ref('');

const roomType = ref<RoomType>('kitchen');
const style = ref<Style>('scandi');
const palette = ref<Palette>('warm');
const strength = ref<Strength>('BALANCED');
const keepLayout = ref(true);
const rearrangeFurniture = ref(true);
const changeElectronics = ref(true);
const removeWatermarks = ref(true);
const interiorCustomInstructions = ref('');
const interiorAspectRatio = ref<AspectRatio>(DEFAULT_ASPECT_RATIO);
const interiorResolution = ref<ResolutionTier>(DEFAULT_RESOLUTION);
const interiorOriginalAspectRatio = ref<AspectRatio | null>(null);

const outdoorType = ref<OutdoorType>('garden');
const outdoorStyle = ref<OutdoorStyle>('modern');
const outdoorPalette = ref<Palette>('earthy');
const outdoorStrength = ref<Strength>('BALANCED');
const outdoorKeepLayout = ref(true);
const outdoorCustomInstructions = ref('');
const outdoorAspectRatio = ref<AspectRatio>(DEFAULT_ASPECT_RATIO);
const outdoorResolution = ref<ResolutionTier>(DEFAULT_RESOLUTION);
const outdoorOriginalAspectRatio = ref<AspectRatio | null>(null);

const manualAspectRatio = ref<AspectRatio>(DEFAULT_ASPECT_RATIO);
const manualResolution = ref<ResolutionTier>(DEFAULT_RESOLUTION);
const manualOriginalAspectRatio = ref<AspectRatio | null>(null);

const busy = ref(false);
const manageBusy = ref(false);
const errorMessage = ref('');
const creditsDialogOpen = ref(false);
const creditsError = ref('');
const checkoutOpen = ref(false);
const checkoutPlan = ref<string | null>(null);
const historyView = ref<'history' | 'saved'>('history');
const historySection = ref<HTMLElement | null>(null);

const howItWorksSteps = computed(() => [
  t('howItWorks.step1'),
  t('howItWorks.step2'),
  t('howItWorks.step3'),
  t('howItWorks.step4'),
]);

type UploadItem = {
  file: File;
  path: string;
  uploading: boolean;
  uploaded: boolean;
  describing?: boolean;
  description?: string;
  descriptionError?: string;
  describePromise?: Promise<void>;
  error?: string;
  promise?: Promise<void>;
};

type UploadState = {
  jobId: string;
  items: UploadItem[];
};

const interiorUploadState = ref<UploadState | null>(null);
const outdoorUploadState = ref<UploadState | null>(null);
const manualUploadState = ref<UploadState | null>(null);

const userCredits = ref<UserCredits | null>(null);

const subscriptionPlans: SubscriptionPlan[] = [
  { id: 'basic', credits: 15, priceEUR: 15 },
  { id: 'standard', credits: 30, priceEUR: 30, allow4k: true, popular: true },
  { id: 'enterprise', credits: 0, priceEUR: null, contactOnly: true },
];

const HISTORY_PAGE_SIZE = 10;
const SAVED_PAGE_SIZE = 8;
const MAX_UPLOAD_MB = 25;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
const latestHistory = ref<JobWithId[]>([]);
const olderHistory = ref<JobWithId[]>([]);
const extraHistory = ref<JobWithId[]>([]);
const historyCursor = shallowRef<QueryDocumentSnapshot<DocumentData> | null>(
  null,
);
const historyLoading = ref(false);
const historyHasMore = ref(true);
const activeJobId = ref<string | null>(null);
const savedImages = ref<SavedImage[]>([]);
const savedCursor = shallowRef<QueryDocumentSnapshot<DocumentData> | null>(
  null,
);
const savedLoading = ref(false);
const savedHasMore = ref(true);
const savedInitialized = ref(false);
const history = computed(() => {
  const map = new Map<string, JobWithId>();
  latestHistory.value.forEach((job) => map.set(job.id, job));
  olderHistory.value.forEach((job) => {
    if (!map.has(job.id)) {
      map.set(job.id, job);
    }
  });
  extraHistory.value.forEach((job) => {
    if (!map.has(job.id)) {
      map.set(job.id, job);
    }
  });
  return Array.from(map.values());
});
const historyCount = computed(() => history.value.length);
const savedCount = computed(() => savedImages.value.length);
const historyTotal = computed(
  () => userCredits.value?.historyCount ?? historyCount.value,
);
const savedTotal = computed(
  () => userCredits.value?.savedCount ?? savedCount.value,
);
const savedPaths = computed(() =>
  savedImages.value.map((item) => item.imagePath),
);
const hasPurchased = computed(() => {
  const planId = userCredits.value?.planId;
  return planId
    ? planId !== 'free'
    : (userCredits.value?.hasPurchased ?? false);
});
const currentPlanId = computed(
  () => (userCredits.value?.planId ?? 'free') as SubscriptionPlan['id'],
);
const billingPeriodEnd = computed(
  () => userCredits.value?.billingPeriodEnd ?? null,
);
const hasActiveSubscription = computed(
  () => currentPlanId.value === 'basic' || currentPlanId.value === 'standard',
);
const allow4k = computed(
  () => isAdminUser.value || (userCredits.value?.allow4k ?? false),
);
const shouldOptimizeUploads = computed(() => {
  if (!isAuthenticated.value) return false;
  if (!userCredits.value) return false;
  if (hasPurchased.value) return false;
  return userCredits.value.isFreeTrialUsed === false;
});
const isAdminUser = computed(() => userCredits.value?.admin === true);
const showAdmin = computed(() => isAdminRoute.value && isAdminUser.value);

let historyUnsub: Unsubscribe | null = null;
let userUnsub: Unsubscribe | null = null;
let adminUnsub: Unsubscribe | null = null;

const roomTypeItems = computed(() =>
  ROOM_TYPES.map((value) => ({ title: t(`roomTypes.${value}`), value })),
);
const styleItems = computed(() =>
  STYLES.map((value) => ({ title: t(`styles.${value}`), value })),
);
const outdoorTypeItems = computed(() =>
  OUTDOOR_TYPES.map((value) => ({ title: t(`outdoorTypes.${value}`), value })),
);
const outdoorStyleItems = computed(() =>
  OUTDOOR_STYLES.map((value) => ({
    title: t(`outdoorStyles.${value}`),
    value,
  })),
);
const paletteItems = computed(() =>
  PALETTES.map((value) => ({ title: t(`palettes.${value}`), value })),
);
const strengthItems = computed(() =>
  STRENGTHS.map((value) => ({ title: t(`strengths.${value}`), value })),
);
const aspectRatioOptions = ASPECT_RATIOS.map((value) => {
  const [width, height] = value.split(':').map(Number);
  return { value, ratio: width / height };
});

function buildAspectRatioItems(original: AspectRatio | null) {
  return ASPECT_RATIOS.map((value) => ({
    title: value,
    value,
    subtitle: original === value ? t('form.sameAsOriginal') : undefined,
  }));
}

const interiorAspectRatioItems = computed(() =>
  buildAspectRatioItems(interiorOriginalAspectRatio.value),
);
const outdoorAspectRatioItems = computed(() =>
  buildAspectRatioItems(outdoorOriginalAspectRatio.value),
);
const manualAspectRatioItems = computed(() =>
  buildAspectRatioItems(manualOriginalAspectRatio.value),
);
const resolutionItems = computed(() =>
  RESOLUTION_TIERS.map((value) => {
    const is4k = value === '4k';
    const disabled = is4k && !allow4k.value;
    return {
      title: t(`resolutions.${value}.title`),
      subtitle: disabled
        ? t('resolutions.4k.locked')
        : t(`resolutions.${value}.subtitle`),
      value,
      disabled,
    };
  }),
);

const fileRules = [
  (value: File[] | File | null) => {
    const count = Array.isArray(value) ? value.length : value ? 1 : 0;
    return count >= 1 ? true : t('form.errors.minPhotos');
  },
  (value: File[] | File | null) => {
    const count = Array.isArray(value) ? value.length : value ? 1 : 0;
    return count <= 2 ? true : t('form.errors.maxPhotos');
  },
  (value: File[] | File | null) => {
    const files = Array.isArray(value) ? value : value ? [value] : [];
    const tooLarge = files.some((file) => file.size > MAX_UPLOAD_BYTES);
    return tooLarge
      ? t('form.errors.maxFileSize', { size: MAX_UPLOAD_MB })
      : true;
  },
];

const isProcessing = computed(() =>
  history.value.some(
    (job) => job.status === 'processing' || job.status === 'queued',
  ),
);
const credits = computed(() => userCredits.value?.credits ?? 0);
const hasCredits = computed(() => credits.value > 0);
const activeResolution = computed<ResolutionTier>(() =>
  generationType.value === 'outdoor'
    ? outdoorResolution.value
    : generationType.value === 'manual'
      ? manualResolution.value
      : interiorResolution.value,
);
const creditsRequired = computed(() =>
  creditsForResolution(activeResolution.value),
);
const hasEnoughCredits = computed(
  () => isAdminUser.value || credits.value >= creditsRequired.value,
);
const checkoutMessage = computed(() => {
  if (!checkoutPlan.value) return t('checkout.success');
  const planLabel = t(`credits.packages.${checkoutPlan.value}`);
  return t('checkout.successWithPlan', { plan: planLabel });
});
const activeFiles = computed(() =>
  generationType.value === 'outdoor'
    ? outdoorFiles.value
    : generationType.value === 'manual'
      ? manualFiles.value
      : interiorFiles.value,
);
const activeUploadState = computed(() =>
  generationType.value === 'outdoor'
    ? outdoorUploadState.value
    : generationType.value === 'manual'
      ? manualUploadState.value
      : interiorUploadState.value,
);
const activeUploadReady = computed(() => {
  const state = activeUploadState.value;
  if (!state) return false;
  if (state.items.length !== activeFiles.value.length) return false;
  return state.items.every(
    (item) => item.uploaded && !item.uploading && item.path,
  );
});
const interiorUploading = computed(
  () => interiorUploadState.value?.items.map((item) => item.uploading) ?? [],
);
const outdoorUploading = computed(
  () => outdoorUploadState.value?.items.map((item) => item.uploading) ?? [],
);
const manualUploading = computed(
  () => manualUploadState.value?.items.map((item) => item.uploading) ?? [],
);
const promptValid = computed(
  () =>
    generationType.value !== 'manual' || manualPrompt.value.trim().length >= 3,
);
const previewBefore = computed(() =>
  previewType.value === 'outdoor' ? outdoorDemoBefore : demoBefore,
);
const previewAfter = computed(() =>
  previewType.value === 'outdoor' ? outdoorDemoAfter : demoAfter,
);
const previewBeforeSet = computed(() => {
  if (previewType.value === 'outdoor') {
    return `${outdoorDemoBefore512} 512w, ${outdoorDemoBefore768} 768w, ${outdoorDemoBefore} 1024w`;
  }
  return `${demoBefore512} 512w, ${demoBefore768} 768w, ${demoBefore} 1024w`;
});
const previewAfterSet = computed(() => {
  if (previewType.value === 'outdoor') {
    return `${outdoorDemoAfter512} 512w, ${outdoorDemoAfter768} 768w, ${outdoorDemoAfter} 1024w`;
  }
  return `${demoAfter512} 512w, ${demoAfter768} 768w, ${demoAfter} 1024w`;
});
const previewSizes = '(max-width: 900px) 100vw, 760px';
const canGenerate = computed(
  () =>
    !busy.value &&
    !isProcessing.value &&
    isAuthenticated.value &&
    hasEnoughCredits.value &&
    promptValid.value &&
    activeUploadReady.value &&
    activeFiles.value.length >= 1 &&
    activeFiles.value.length <= 2,
);

function scrollToHistory(view: 'history' | 'saved') {
  historyView.value = view;
  nextTick(() => {
    historySection.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  });
}

function navigateAdmin() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/admin') {
    window.history.pushState({}, '', '/admin');
    syncAdminRoute();
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function navigateHome() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/') {
    window.history.pushState({}, '', '/');
    syncAdminRoute();
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

async function ensureJobInHistory(jobId: string) {
  if (history.value.some((job) => job.id === jobId)) return;
  const job = await fetchJobById(jobId);
  if (!job) return;
  extraHistory.value = [job, ...extraHistory.value];
}

async function handleSaveImage(payload: { imagePath: string; jobId: string }) {
  if (!uid.value) return;
  await saveImage(uid.value, payload.imagePath, payload.jobId);
  const id = savedImageDocId(payload.imagePath);
  if (savedImages.value.some((item) => item.id === id)) return;
  savedImages.value = [
    { id, imagePath: payload.imagePath, jobId: payload.jobId },
    ...savedImages.value,
  ];
}

async function handleRemoveImage(payload: { imagePath: string }) {
  if (!uid.value) return;
  await removeSavedImage(uid.value, payload.imagePath);
  const id = savedImageDocId(payload.imagePath);
  savedImages.value = savedImages.value.filter((item) => item.id !== id);
}

async function openSavedDetails(jobId: string) {
  await ensureJobInHistory(jobId);
  activeJobId.value = jobId;
}

function creditsForResolution(value: ResolutionTier): number {
  if (value === '4k') return 2;
  return 1;
}

function normalizeFiles(value: File[] | File | null): File[] {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
}

function findClosestAspectRatio(width: number, height: number): AspectRatio {
  const target = width / height;
  let closest = DEFAULT_ASPECT_RATIO;
  let minDiff = Number.POSITIVE_INFINITY;
  for (const option of aspectRatioOptions) {
    const diff = Math.abs(option.ratio - target);
    if (diff < minDiff) {
      minDiff = diff;
      closest = option.value;
    }
  }
  return closest;
}

function validateUploadFiles(files: File[]): boolean {
  if (!files.length) return true;
  const tooLarge = files.find((file) => file.size > MAX_UPLOAD_BYTES);
  if (tooLarge) {
    errorMessage.value = t('form.errors.maxFileSize', { size: MAX_UPLOAD_MB });
    return false;
  }
  if (errorMessage.value) {
    errorMessage.value = '';
  }
  return true;
}

function loadImageSize(
  file: File,
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      URL.revokeObjectURL(url);
      if (!width || !height) {
        resolve(null);
        return;
      }
      resolve({ width, height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

async function resizeForUpload(
  file: File,
  maxSize: number,
): Promise<Blob | File> {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement | ImageBitmap>(
      (resolve, reject) => {
        if ('createImageBitmap' in window) {
          createImageBitmap(file)
            .then(resolve)
            .catch(() => {
              const img = new Image();
              img.onload = () => resolve(img);
              img.onerror = () => reject(new Error('Image load failed'));
              img.src = url;
            });
          return;
        }
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = url;
      },
    );

    const width =
      image instanceof ImageBitmap
        ? image.width
        : image.naturalWidth || image.width;
    const height =
      image instanceof ImageBitmap
        ? image.height
        : image.naturalHeight || image.height;
    const maxDim = Math.max(width, height);
    if (!width || !height || maxDim <= maxSize) return file;

    const scale = maxSize / maxDim;
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(image as CanvasImageSource, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.85);
    });
    return blob ?? file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function prepareUploadBlob(file: File): Promise<Blob | File> {
  if (!shouldOptimizeUploads.value) return file;
  return resizeForUpload(file, 2048);
}

function buildUploadState(): UploadState {
  return { jobId: doc(collection(db, 'jobs')).id, items: [] };
}

function withTimeoutFallback<T>(
  promise: Promise<T>,
  ms: number,
  fallback: () => T,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => resolve(fallback()), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string) {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(message));
    }, ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

function startDescribeForItem(
  item: UploadItem,
  path: string,
  index: number,
  stateRef: { value: UploadState | null },
) {
  item.describing = true;
  item.describePromise = withTimeoutFallback(
    describeImage(path),
    12000,
    () => '',
  )
    .then((description) => {
      if (!description) return;
      if (stateRef.value?.items[index] !== item) return;
      item.description = description;
    })
    .catch((error) => {
      item.descriptionError =
        error instanceof Error ? error.message : 'Describe failed';
    })
    .finally(() => {
      if (stateRef.value?.items[index] !== item) return;
      item.describing = false;
    });
}

function clearUploadState(
  stateRef: { value: UploadState | null },
  deleteFiles: boolean,
) {
  const state = stateRef.value;
  if (state && deleteFiles) {
    state.items.forEach((item) => {
      if (item.path) {
        deleteStoragePath(item.path).catch(() => null);
      }
    });
  }
  stateRef.value = null;
}

function startUploadForItem(
  file: File,
  index: number,
  stateRef: { value: UploadState | null },
): UploadItem {
  const item = reactive<UploadItem>({
    file,
    path: '',
    uploading: true,
    uploaded: false,
  });
  const currentUid = uid.value;
  const jobId = stateRef.value?.jobId;
  if (!currentUid || !jobId) {
    item.uploading = false;
    item.error = 'Missing user';
    return item;
  }

  item.promise = (async () => {
    const uploadBlob = await withTimeoutFallback(
      prepareUploadBlob(file),
      4000,
      () => file,
    );
    const path = await withTimeout(
      uploadInput(uploadBlob, currentUid, jobId, index),
      60000,
      'Upload timeout',
    );
    if (stateRef.value?.items[index] !== item) {
      await deleteStoragePath(path).catch(() => null);
      return;
    }
    item.path = path;
    item.uploaded = true;
    startDescribeForItem(item, path, index, stateRef);
  })()
    .catch((error) => {
      item.error = error instanceof Error ? error.message : 'Upload failed';
    })
    .finally(() => {
      item.uploading = false;
    });

  return item;
}

function syncPreuploads(
  files: File[],
  stateRef: { value: UploadState | null },
) {
  if (!uid.value) {
    clearUploadState(stateRef, false);
    return;
  }

  if (files.length === 0) {
    clearUploadState(stateRef, true);
    return;
  }

  if (!stateRef.value) {
    stateRef.value = buildUploadState();
  }

  const state = stateRef.value;
  const nextItems: UploadItem[] = [];

  files.forEach((file, index) => {
    const existing = state.items[index];
    if (existing && existing.file === file) {
      nextItems[index] = existing;
      return;
    }
    if (existing?.path) {
      deleteStoragePath(existing.path).catch(() => null);
    }
    nextItems[index] = startUploadForItem(file, index, stateRef);
  });

  if (state.items.length > files.length) {
    state.items.slice(files.length).forEach((item) => {
      if (item?.path) {
        deleteStoragePath(item.path).catch(() => null);
      }
    });
  }

  state.items = nextItems;
}

async function syncAspectRatioWithFile(
  file: File,
  filesRef: { value: File[] },
  aspectRatioRef: { value: AspectRatio },
  originalRef: { value: AspectRatio | null },
) {
  const size = await loadImageSize(file);
  if (!size) return;
  if (!filesRef.value.length || filesRef.value[0] !== file) return;
  const closest = findClosestAspectRatio(size.width, size.height);
  aspectRatioRef.value = closest;
  originalRef.value = closest;
}

function chipColor(status: JobWithId['status']): string {
  if (status === 'done') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'processing') return 'info';
  return 'warning';
}

function updateInteriorFiles(value: File[] | File | null) {
  const files = normalizeFiles(value);
  if (!validateUploadFiles(files)) {
    interiorFiles.value = [];
    interiorOriginalAspectRatio.value = null;
    syncPreuploads([], interiorUploadState);
    return;
  }
  interiorFiles.value = files;
  if (files.length === 0) {
    interiorOriginalAspectRatio.value = null;
    syncPreuploads([], interiorUploadState);
    return;
  }
  void syncAspectRatioWithFile(
    files[0],
    interiorFiles,
    interiorAspectRatio,
    interiorOriginalAspectRatio,
  );
  syncPreuploads(files, interiorUploadState);
}

function updateOutdoorFiles(value: File[] | File | null) {
  const files = normalizeFiles(value);
  if (!validateUploadFiles(files)) {
    outdoorFiles.value = [];
    outdoorOriginalAspectRatio.value = null;
    syncPreuploads([], outdoorUploadState);
    return;
  }
  outdoorFiles.value = files;
  if (files.length === 0) {
    outdoorOriginalAspectRatio.value = null;
    syncPreuploads([], outdoorUploadState);
    return;
  }
  void syncAspectRatioWithFile(
    files[0],
    outdoorFiles,
    outdoorAspectRatio,
    outdoorOriginalAspectRatio,
  );
  syncPreuploads(files, outdoorUploadState);
}

function updateManualFiles(value: File[] | File | null) {
  const files = normalizeFiles(value);
  if (!validateUploadFiles(files)) {
    manualFiles.value = [];
    manualOriginalAspectRatio.value = null;
    syncPreuploads([], manualUploadState);
    return;
  }
  manualFiles.value = files;
  if (files.length === 0) {
    manualOriginalAspectRatio.value = null;
    syncPreuploads([], manualUploadState);
    return;
  }
  void syncAspectRatioWithFile(
    files[0],
    manualFiles,
    manualAspectRatio,
    manualOriginalAspectRatio,
  );
  syncPreuploads(files, manualUploadState);
}

function updateManualPrompt(value: string) {
  manualPrompt.value = value;
}

function updateRoomType(value: RoomType) {
  roomType.value = value;
}

function updateStyle(value: Style) {
  style.value = value;
}

function updatePalette(value: Palette) {
  palette.value = value;
}

function updateStrength(value: Strength) {
  strength.value = value;
}

function updateKeepLayout(value: boolean) {
  keepLayout.value = value;
}

function updateRearrangeFurniture(value: boolean) {
  rearrangeFurniture.value = value;
}

function updateChangeElectronics(value: boolean) {
  changeElectronics.value = value;
}

function updateRemoveWatermarks(value: boolean) {
  removeWatermarks.value = value;
}

function updateInteriorCustomInstructions(value: string) {
  interiorCustomInstructions.value = value;
}

function updateInteriorAspectRatio(value: AspectRatio) {
  interiorAspectRatio.value = value;
}

function updateInteriorResolution(value: ResolutionTier) {
  interiorResolution.value = value;
}

function updateOutdoorType(value: OutdoorType) {
  outdoorType.value = value;
}

function updateOutdoorStyle(value: OutdoorStyle) {
  outdoorStyle.value = value;
}

function updateOutdoorPalette(value: Palette) {
  outdoorPalette.value = value;
}

function updateOutdoorStrength(value: Strength) {
  outdoorStrength.value = value;
}

function updateOutdoorKeepLayout(value: boolean) {
  outdoorKeepLayout.value = value;
}

function updateOutdoorCustomInstructions(value: string) {
  outdoorCustomInstructions.value = value;
}

function updateOutdoorAspectRatio(value: AspectRatio) {
  outdoorAspectRatio.value = value;
}

function updateOutdoorResolution(value: ResolutionTier) {
  outdoorResolution.value = value;
}

function updateManualAspectRatio(value: AspectRatio) {
  manualAspectRatio.value = value;
}

function updateManualResolution(value: ResolutionTier) {
  manualResolution.value = value;
}

function resetForm() {
  if (generationType.value === 'outdoor') {
    outdoorFiles.value = [];
    outdoorType.value = 'garden';
    outdoorStyle.value = 'modern';
    outdoorPalette.value = 'earthy';
    outdoorStrength.value = 'BALANCED';
    outdoorKeepLayout.value = true;
    outdoorCustomInstructions.value = '';
    outdoorAspectRatio.value = DEFAULT_ASPECT_RATIO;
    outdoorResolution.value = DEFAULT_RESOLUTION;
    clearUploadState(outdoorUploadState, false);
  } else if (generationType.value === 'manual') {
    manualFiles.value = [];
    manualPrompt.value = '';
    manualAspectRatio.value = DEFAULT_ASPECT_RATIO;
    manualResolution.value = DEFAULT_RESOLUTION;
    clearUploadState(manualUploadState, false);
  } else {
    interiorFiles.value = [];
    roomType.value = 'kitchen';
    style.value = 'scandi';
    palette.value = 'warm';
    strength.value = 'BALANCED';
    keepLayout.value = true;
    rearrangeFurniture.value = true;
    changeElectronics.value = true;
    removeWatermarks.value = true;
    interiorCustomInstructions.value = '';
    interiorAspectRatio.value = DEFAULT_ASPECT_RATIO;
    interiorResolution.value = DEFAULT_RESOLUTION;
    clearUploadState(interiorUploadState, false);
  }
  errorMessage.value = '';
}

async function handleGenerate() {
  if (!uid.value || busy.value) return;
  if (!hasEnoughCredits.value) {
    openCreditsDialog();
    return;
  }
  busy.value = true;
  errorMessage.value = '';

  try {
    const uploadState = activeUploadState.value;
    const jobId = uploadState?.jobId ?? doc(collection(db, 'jobs')).id;
    let imagePaths: string[] = [];
    if (uploadState) {
      await Promise.all(uploadState.items.map((item) => item.promise));
      const hasError = uploadState.items.some((item) => item.error);
      if (hasError) {
        throw new Error(t('errors.createJobFailed'));
      }
      await Promise.all(
        uploadState.items.map((item) =>
          item.describePromise
            ? withTimeoutFallback(item.describePromise, 8000, () => undefined)
            : Promise.resolve(),
        ),
      );
      imagePaths = uploadState.items.map((item) => item.path).filter(Boolean);
      if (imagePaths.length !== activeFiles.value.length) {
        throw new Error(t('errors.createJobFailed'));
      }
    } else {
      imagePaths = await uploadInputs(activeFiles.value, uid.value, jobId);
    }
    const interiorCustom = interiorCustomInstructions.value.trim();
    const outdoorCustom = outdoorCustomInstructions.value.trim();
    const inputDescription = uploadState?.items[0]?.description;
    const contextDescription = uploadState?.items[1]?.description;
    const descriptionPayload = {
      ...(inputDescription ? { inputDescription } : {}),
      ...(contextDescription ? { contextDescription } : {}),
    };

    const createdJobId =
      generationType.value === 'manual'
        ? await createJob({
            generationType: 'manual',
            prompt: manualPrompt.value.trim(),
            aspectRatio: manualAspectRatio.value,
            resolution: manualResolution.value,
            imagePaths,
            jobId,
            ...descriptionPayload,
          })
        : generationType.value === 'outdoor'
          ? await createJob({
              generationType: 'outdoor',
              outdoorType: outdoorType.value,
              style: outdoorStyle.value,
              palette: outdoorPalette.value,
              strength: outdoorStrength.value,
              keepLayout: outdoorKeepLayout.value,
              aspectRatio: outdoorAspectRatio.value,
              resolution: outdoorResolution.value,
              ...(outdoorCustom ? { customInstructions: outdoorCustom } : {}),
              imagePaths,
              jobId,
              ...descriptionPayload,
            })
          : await createJob({
              generationType: 'interior',
              roomType: roomType.value,
              style: style.value,
              palette: palette.value,
              strength: strength.value,
              keepLayout: keepLayout.value,
              rearrangeFurniture: rearrangeFurniture.value,
              changeElectronics: changeElectronics.value,
              removeWatermarks: removeWatermarks.value,
              aspectRatio: interiorAspectRatio.value,
              resolution: interiorResolution.value,
              ...(interiorCustom ? { customInstructions: interiorCustom } : {}),
              imagePaths,
              jobId,
              ...descriptionPayload,
            });

    if (createdJobId) {
      activeJobId.value = createdJobId;
      resetForm();
      // No-op: history subscription will surface the new job.
    }
  } catch (error) {
    const message =
      error instanceof Error ? error.message : t('errors.createJobFailed');
    errorMessage.value = message;
    if (message.toLowerCase().includes('credit')) {
      openCreditsDialog();
    }
  } finally {
    busy.value = false;
  }
}

async function handleRetry(job: JobWithId) {
  if (!uid.value || busy.value) return;
  if (isProcessing.value) {
    errorMessage.value = t('form.alerts.processing');
    return;
  }
  const resolution = job.inputs.resolution ?? DEFAULT_RESOLUTION;
  const requiredCredits = creditsForResolution(resolution);
  if (credits.value < requiredCredits) {
    openCreditsDialog();
    return;
  }
  if (!job.inputs.imagePaths?.length) return;

  busy.value = true;
  errorMessage.value = '';
  try {
    const basePayload = {
      imagePaths: job.inputs.imagePaths,
      aspectRatio: job.inputs.aspectRatio ?? DEFAULT_ASPECT_RATIO,
      resolution: job.inputs.resolution ?? DEFAULT_RESOLUTION,
      ...(job.inputs.editInstruction
        ? { editInstruction: job.inputs.editInstruction }
        : {}),
      ...(job.inputs.inputDescription
        ? { inputDescription: job.inputs.inputDescription }
        : {}),
      ...(job.inputs.contextDescription
        ? { contextDescription: job.inputs.contextDescription }
        : {}),
    };

    const createdJobId =
      job.inputs.generationType === 'manual'
        ? await createJob({
            ...basePayload,
            generationType: 'manual',
            prompt: job.inputs.prompt,
          })
        : job.inputs.generationType === 'outdoor'
          ? await createJob({
              ...basePayload,
              generationType: 'outdoor',
              outdoorType: job.inputs.outdoorType,
              style: job.inputs.style,
              palette: job.inputs.palette,
              strength: job.inputs.strength,
              keepLayout: job.inputs.keepLayout,
              ...(job.inputs.customInstructions
                ? { customInstructions: job.inputs.customInstructions }
                : {}),
            })
          : await createJob({
              ...basePayload,
              generationType: 'interior',
              roomType: job.inputs.roomType,
              style: job.inputs.style,
              palette: job.inputs.palette,
              strength: job.inputs.strength,
              keepLayout: job.inputs.keepLayout,
              rearrangeFurniture: job.inputs.rearrangeFurniture ?? false,
              changeElectronics: job.inputs.changeElectronics ?? false,
              removeWatermarks: job.inputs.removeWatermarks ?? false,
              ...(job.inputs.customInstructions
                ? { customInstructions: job.inputs.customInstructions }
                : {}),
            });

    if (createdJobId) {
      historyView.value = 'history';
      activeJobId.value = createdJobId;
    }
  } catch (error) {
    const message =
      error instanceof Error ? error.message : t('errors.createJobFailed');
    errorMessage.value = message;
    if (message.toLowerCase().includes('credit')) {
      openCreditsDialog();
    }
  } finally {
    busy.value = false;
  }
}

async function handleDeleteJob(job: JobWithId) {
  if (!uid.value || busy.value) return;
  if (job.status === 'processing' || job.status === 'queued') return;
  try {
    await deleteJob(job.id);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : t('errors.createJobFailed');
    errorMessage.value = message;
  } finally {
    latestHistory.value = latestHistory.value.filter(
      (item) => item.id !== job.id,
    );
    olderHistory.value = olderHistory.value.filter(
      (item) => item.id !== job.id,
    );
    extraHistory.value = extraHistory.value.filter(
      (item) => item.id !== job.id,
    );
    if (activeJobId.value === job.id) {
      activeJobId.value = null;
    }
  }
}

async function handleEditGenerate(payload: {
  job: JobWithId;
  imagePath: string;
  instructions: string;
}) {
  if (!uid.value || busy.value) return;
  const resolution = payload.job.inputs.resolution ?? DEFAULT_RESOLUTION;
  const requiredCredits = creditsForResolution(resolution);
  if (credits.value < requiredCredits) {
    openCreditsDialog();
    return;
  }
  busy.value = true;
  errorMessage.value = '';

  try {
    const inputs = payload.job.inputs;
    let editDescription: string | undefined;
    try {
      editDescription = await withTimeoutFallback(
        describeImage(payload.imagePath),
        8000,
        () => undefined,
      );
    } catch (error) {
      editDescription = undefined;
    }
    const basePayload = {
      imagePaths: [payload.imagePath],
      editInstruction: payload.instructions,
      aspectRatio: payload.job.inputs.aspectRatio ?? DEFAULT_ASPECT_RATIO,
      resolution,
      ...(editDescription ? { inputDescription: editDescription } : {}),
    };

    const createdJobId =
      inputs.generationType === 'manual'
        ? await createJob({
            ...basePayload,
            generationType: 'manual',
            prompt: inputs.prompt,
          })
        : inputs.generationType === 'outdoor'
          ? await createJob({
              ...basePayload,
              generationType: 'outdoor',
              outdoorType: inputs.outdoorType,
              style: inputs.style,
              palette: inputs.palette,
              strength: inputs.strength,
              keepLayout: inputs.keepLayout,
              aspectRatio: inputs.aspectRatio ?? DEFAULT_ASPECT_RATIO,
              resolution: inputs.resolution ?? DEFAULT_RESOLUTION,
            })
          : await createJob({
              ...basePayload,
              generationType: 'interior',
              roomType: inputs.roomType,
              style: inputs.style,
              palette: inputs.palette,
              strength: inputs.strength,
              keepLayout: inputs.keepLayout,
              rearrangeFurniture: inputs.rearrangeFurniture ?? false,
              changeElectronics: inputs.changeElectronics ?? false,
              removeWatermarks: inputs.removeWatermarks ?? false,
              aspectRatio: inputs.aspectRatio ?? DEFAULT_ASPECT_RATIO,
              resolution: inputs.resolution ?? DEFAULT_RESOLUTION,
            });

    generationType.value = inputs.generationType ?? 'interior';
    if (createdJobId) {
      activeJobId.value = createdJobId;
    }
  } catch (error) {
    const message =
      error instanceof Error ? error.message : t('errors.createJobFailed');
    errorMessage.value = message;
    if (message.toLowerCase().includes('credit')) {
      openCreditsDialog();
    }
  } finally {
    busy.value = false;
  }
}

function openCreditsDialog() {
  creditsError.value = '';
  creditsDialogOpen.value = true;
}

function openHowItWorks() {
  howItWorksOpen.value = true;
}

async function handleBuyCredits(planId: SubscriptionPlan['id']) {
  creditsError.value = '';
  if (planId === 'enterprise' || planId === 'free') {
    window.location.href = `mailto:refitgpt@seivit.app?subject=Enterprise%20plan`;
    return;
  }
  if (hasActiveSubscription.value && planId !== currentPlanId.value) {
    await handleManageSubscription();
    return;
  }
  try {
    const url = await createCheckoutSession(planId, window.location.origin);
    window.location.assign(url);
  } catch (error) {
    creditsError.value = t('credits.checkoutError');
    creditsDialogOpen.value = true;
  }
}

async function handleManageSubscription() {
  creditsError.value = '';
  if (!hasActiveSubscription.value) return;
  manageBusy.value = true;
  try {
    const url = await createPortalSession(window.location.origin);
    window.location.assign(url);
  } catch (error) {
    creditsError.value = t('credits.checkoutError');
    creditsDialogOpen.value = true;
  } finally {
    manageBusy.value = false;
  }
}

function syncAdminRoute() {
  if (typeof window === 'undefined') return;
  isAdminRoute.value = window.location.pathname === '/admin';
  updateMeta();
}

onMounted(async () => {
  syncAdminRoute();
  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', syncAdminRoute);
  }
  const initialUser = await waitForAuthReady();
  if (initialUser) {
    uid.value = initialUser.uid;
    isAnonymous.value = initialUser.isAnonymous;
    userLabel.value = initialUser.displayName || initialUser.email || null;
    userAvatar.value = initialUser.photoURL || null;
    if (!initialUser.isAnonymous) {
      await ensureUser().catch(() => {});
    }
  }
  observeAuth((user) => {
    if (!user) {
      userLabel.value = null;
      isAnonymous.value = true;
      uid.value = null;
      userAvatar.value = null;
      userCredits.value = null;
      adminStats.value = null;
      if (adminUnsub) {
        adminUnsub();
        adminUnsub = null;
      }
      return;
    }
    uid.value = user.uid;
    isAnonymous.value = user.isAnonymous;
    userLabel.value = user.displayName || user.email || null;
    userAvatar.value = user.photoURL || null;
    if (!user.isAnonymous) {
      ensureUser().catch(() => {});
    }
  });

  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    const langParam = url.searchParams.get('lang');
    if (langParam && SUPPORTED_LOCALES.includes(langParam as SupportedLocale)) {
      setLocale(langParam as SupportedLocale);
      url.searchParams.delete('lang');
    }
    const checkout = url.searchParams.get('checkout');
    if (checkout === 'success') {
      const planParam = url.searchParams.get('plan');
      checkoutPlan.value = planParam ? decodeURIComponent(planParam) : null;
      checkoutOpen.value = true;
    }
    if (checkout) {
      url.searchParams.delete('checkout');
      url.searchParams.delete('plan');
    }
    if (checkout || langParam) {
      window.history.replaceState({}, '', url.toString());
    }
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('popstate', syncAdminRoute);
  }
  if (adminUnsub) {
    adminUnsub();
    adminUnsub = null;
  }
});

async function handleGoogleLogin() {
  await signInWithGoogle();
}

async function handleSignOut() {
  await signOutUser();
}

async function loadMoreHistory() {
  if (!uid.value || historyLoading.value || !historyHasMore.value) return;
  historyLoading.value = true;
  try {
    const { jobs, lastDoc } = await fetchHistoryPage(
      uid.value,
      HISTORY_PAGE_SIZE,
      historyCursor.value,
    );
    if (jobs.length === 0) {
      historyHasMore.value = false;
      return;
    }
    const existing = new Set(history.value.map((job) => job.id));
    const newJobs = jobs.filter((job) => !existing.has(job.id));
    olderHistory.value = [...olderHistory.value, ...newJobs];
    historyCursor.value = lastDoc;
    historyHasMore.value = jobs.length === HISTORY_PAGE_SIZE;
  } finally {
    historyLoading.value = false;
  }
}

async function loadMoreSavedImages() {
  if (!uid.value || savedLoading.value || !savedHasMore.value) return;
  savedLoading.value = true;
  try {
    const { items, lastDoc } = await fetchSavedImagesPage(
      uid.value,
      SAVED_PAGE_SIZE,
      savedCursor.value,
    );
    if (items.length === 0) {
      savedHasMore.value = false;
      return;
    }
    const existing = new Set(savedImages.value.map((item) => item.id));
    const newItems = items.filter((item) => !existing.has(item.id));
    savedImages.value = [...savedImages.value, ...newItems];
    savedCursor.value = lastDoc;
    savedHasMore.value = items.length === SAVED_PAGE_SIZE;
    savedInitialized.value = true;
  } finally {
    savedLoading.value = false;
  }
}

watch(uid, (value) => {
  historyUnsub?.();
  latestHistory.value = [];
  olderHistory.value = [];
  extraHistory.value = [];
  historyCursor.value = null;
  historyLoading.value = false;
  historyHasMore.value = true;
  if (!value) return;
  historyUnsub = subscribeHistory(value, HISTORY_PAGE_SIZE, (jobs, lastDoc) => {
    latestHistory.value = jobs;
    if (!olderHistory.value.length) {
      historyCursor.value = lastDoc;
      historyHasMore.value = jobs.length === HISTORY_PAGE_SIZE;
    }
  });
});

watch(uid, (value) => {
  savedImages.value = [];
  savedCursor.value = null;
  savedLoading.value = false;
  savedHasMore.value = true;
  savedInitialized.value = false;
  if (!value) return;
  if (historyView.value === 'saved') {
    void loadMoreSavedImages();
  }
});

watch(uid, (value) => {
  if (!value) {
    clearUploadState(interiorUploadState, false);
    clearUploadState(outdoorUploadState, false);
    clearUploadState(manualUploadState, false);
    return;
  }
  syncPreuploads(interiorFiles.value, interiorUploadState);
  syncPreuploads(outdoorFiles.value, outdoorUploadState);
  syncPreuploads(manualFiles.value, manualUploadState);
});

watch(historyView, (value) => {
  if (value !== 'saved') return;
  if (!uid.value || savedInitialized.value) return;
  void loadMoreSavedImages();
});

function ensureMetaTag(name: string): HTMLMetaElement {
  let tag = document.querySelector(
    `meta[name="${name}"]`,
  ) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  return tag;
}

function ensureMetaProperty(property: string): HTMLMetaElement {
  let tag = document.querySelector(
    `meta[property="${property}"]`,
  ) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  return tag;
}

function ensureCanonicalLink(): HTMLLinkElement {
  let tag = document.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  return tag;
}

function updateMeta() {
  if (typeof document === 'undefined') return;
  const title = t('meta.title');
  const description = t('meta.description');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const url = origin ? `${origin}${path}` : '';
  const ogImage = origin ? `${origin}/og-image.jpg` : '';
  const isAdminPath = path === '/admin';

  document.title = title;
  ensureMetaTag('description').setAttribute('content', description);
  ensureMetaTag('robots').setAttribute(
    'content',
    isAdminPath ? 'noindex,nofollow' : 'index,follow',
  );

  ensureMetaProperty('og:title').setAttribute('content', title);
  ensureMetaProperty('og:description').setAttribute('content', description);
  ensureMetaProperty('og:type').setAttribute('content', 'website');
  if (url) {
    ensureMetaProperty('og:url').setAttribute('content', url);
    ensureCanonicalLink().setAttribute('href', url);
  }
  if (ogImage) {
    ensureMetaProperty('og:image').setAttribute('content', ogImage);
  }

  ensureMetaTag('twitter:card').setAttribute('content', 'summary_large_image');
  ensureMetaTag('twitter:title').setAttribute('content', title);
  ensureMetaTag('twitter:description').setAttribute('content', description);
  if (ogImage) {
    ensureMetaTag('twitter:image').setAttribute('content', ogImage);
  }
}

watch(
  () => locale.value,
  () => updateMeta(),
  { immediate: true },
);

watch(uid, (value) => {
  userUnsub?.();
  userCredits.value = null;
  if (!value) return;
  userUnsub = subscribeUser(value, (user) => {
    userCredits.value = user;
  });
});

watch(showAdmin, (value) => {
  adminUnsub?.();
  adminStats.value = null;
  if (!value) return;
  adminUnsub = subscribeAdminStats((stats) => {
    adminStats.value = stats;
  });
});

watch(creditsDialogOpen, (value) => {
  if (!value) {
    creditsError.value = '';
  }
});

watch(
  isLightTheme,
  (value) => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute(
      'data-theme',
      value ? 'light' : 'dark',
    );
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.page {
  position: relative;
  overflow: hidden;
}

.page-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: var(--page-bg);
  animation: pageGlow 18s ease-in-out infinite alternate;
  pointer-events: none;
}

.page > *:not(.page-bg) {
  position: relative;
  z-index: 1;
}

.page-content {
  padding-top: 2rem;
  padding-bottom: 5rem;
  max-width: var(--app-max-width);
  margin: 0 auto;
  width: 100%;
}

@media (max-width: 900px) {
  // .page-content {
  //   padding-bottom: calc(2rem + env(safe-area-inset-bottom));
  // }
}

.generator-toggle {
  margin: 1rem 0 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
}

.toggle-label {
  font-size: 0.9rem;
  color: var(--app-muted-70);
  letter-spacing: 0.01em;
}

.preview-toggle {
  margin-top: 0;
}

.toggle-group {
  border-radius: 999px;
  background: var(--surface-soft);
  border: 1px solid var(--panel-border);
  /* padding: 0.2rem; */
}

.toggle-group :deep(.v-btn) {
  color: var(--app-muted-80);
  text-transform: none;
  font-weight: 600;
}

.toggle-group :deep(.v-btn.v-btn--active) {
  /* background: var(--brand-gradient) !important; */
  /* color: #fff !important; */
  /* box-shadow: var(--accent-shadow-soft); */
}

@keyframes pageGlow {
  0% {
    filter: hue-rotate(0deg);
    transform: scale(1) translate3d(0, 0, 0);
  }
  50% {
    filter: hue-rotate(10deg);
    transform: scale(1.03) translate3d(-1%, -2%, 0);
  }
  100% {
    filter: hue-rotate(-8deg);
    transform: scale(1.05) translate3d(1%, 1%, 0);
  }
}

.preview-section {
  margin-top: 2.5rem;
}

.how-section {
  margin-top: 2.5rem;
  padding: 2rem;
  border-radius: 22px;
  border: 1px solid var(--panel-border);
  background: var(--surface-strong);
  box-shadow: var(--panel-shadow);
}

.how-section h2 {
  margin-bottom: 1.2rem;
  font-size: 1.6rem;
}

.how-steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 1rem;
}

.how-step {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.9rem;
  align-items: start;
}

.how-step p {
  margin: 0;
  color: var(--app-muted-70);
  line-height: 1.6;
}

.step-index {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 700;
  color: #fff;
  background: var(--brand-gradient);
  box-shadow: var(--accent-shadow-soft);
}

.how-dialog {
  border-radius: var(--modal-radius);
  border: 1px solid var(--panel-border);
  /* background: var(--panel-bg); */
  box-shadow: var(--panel-shadow);
}

.how-steps--dialog {
  gap: 1.1rem;
}

.preview-card {
  border-radius: var(--modal-radius);
  border: 1px solid var(--panel-border);
  background: var(--panel-bg);
  box-shadow: var(--panel-shadow);
  backdrop-filter: blur(16px);
}

.history-section {
  margin-top: 2.5rem;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2.5rem;
  @media (max-width: 600px) {
    flex-direction: column-reverse;
    justify-content: flex-end;
    align-items: flex-start;
  }
}

.history-toggle {
  border-radius: 999px;
  background: var(--surface-soft);
  border: 1px solid var(--panel-border);
}

.history-toggle :deep(.v-btn) {
  text-transform: none;
  font-weight: 600;
}
</style>
